import { z } from 'zod/v4';

/**
 * The three kinds of asset a questionnaire question can link.
 *
 * Declared here rather than beside the queries that use it because `contracts/`
 * must stay free of `core/**` imports — it is the frontend's entry point, and a
 * contract that reaches into `core` drags the server-side module graph into the
 * browser bundle.
 */
export const REQUEST_ASSET_BUCKETS = [
  'document',
  'certificate',
  'audit',
] as const;

export type RequestAssetBucket = (typeof REQUEST_ASSET_BUCKETS)[number];

/**
 * Path parameters of `GET /api/questionnaires/requests/:requestId/assets`.
 */
export const QuestionnaireRequestAssetsParamsSchema = z.object({
  requestId: z.string().min(1).max(64),
});

export type QuestionnaireRequestAssetsParams = z.infer<
  typeof QuestionnaireRequestAssetsParamsSchema
>;

/**
 * Which kind of asset to list. Required: each bucket reads a different table, so
 * there is no sensible "all" — and a picker only ever asks for one.
 */
export const QuestionnaireRequestAssetsQuerySchema = z.object({
  bucket: z.enum(REQUEST_ASSET_BUCKETS),
});

export type QuestionnaireRequestAssetsQuery = z.infer<
  typeof QuestionnaireRequestAssetsQuerySchema
>;

/** The file columns every bucket's detail payload carries, in one shape. */
export const RequestAssetFileSchema = z.object({
  fileId: z.string().nullable(),
  /** The object key, not a signed URL — sign it through the asset-file route. */
  fileUrl: z.string().nullable(),
  fileType: z.string().nullable(),
  contentType: z.string().nullable(),
  /** The uploaded file's own name, which the answer forms label the preview with. */
  fileName: z.string().nullable(),
});

/**
 * The per-bucket half of an asset, discriminated by `bucket`.
 *
 * A tagged payload rather than more flat columns: the three tables share almost
 * nothing past the common core, and flattening their union would hand every
 * consumer a row of mostly-null fields with no way to tell which ones its own
 * bucket populates.
 */
export const RequestAssetDetailsSchema = z.discriminatedUnion('bucket', [
  z.object({
    bucket: z.literal('document'),
    file: RequestAssetFileSchema,
  }),
  z.object({
    bucket: z.literal('certificate'),
    /**
     * The standard's own type — `CERTIFICATE`, `MEMBERSHIP`, `TRANSACTION`, … —
     * which is what an asset-link option matches on, not the standard id.
     */
    standardType: z.string().nullable(),
    status: z.string().nullable(),
    issuedBy: z.string().nullable(),
    documentNumber: z.string().nullable(),
    licenseNumber: z.string().nullable(),
    file: RequestAssetFileSchema,
  }),
  z.object({
    bucket: z.literal('audit'),
    referenceNumber: z.string().nullable(),
    auditedBy: z.string().nullable(),
    facilityScore: z.string().nullable(),
    auditReportFile: RequestAssetFileSchema,
    correctiveActionPlanFile: RequestAssetFileSchema,
  }),
]);

/**
 * One asset: a common core plus the per-bucket detail the answer form renders.
 *
 * The core is what the picker needs — an id to link, a label, and the type that
 * decides which slot it belongs in. `details` is what the form needs once an
 * asset is linked. Both come from one read because they used to come from two,
 * and the second was network-scoped: on a delegated request a saved answer came
 * back blank when the page was reopened.
 *
 * Dates are passed through as whatever the column holds rather than normalised,
 * matching how the sibling request-detail response treats its own dates.
 */
export const QuestionnaireRequestAssetSchema = z.object({
  id: z.string(),
  subTypeId: z.string().nullable(),
  subTypeName: z.string().nullable(),
  name: z.string().nullable(),
  hasFile: z.boolean(),
  issuedAt: z.unknown().nullable(),
  expiresAt: z.unknown().nullable(),
  details: RequestAssetDetailsSchema,
});

export type QuestionnaireRequestAsset = z.infer<
  typeof QuestionnaireRequestAssetSchema
>;

export const QuestionnaireRequestAssetsResponseSchema = z.object({
  assets: z.array(QuestionnaireRequestAssetSchema),
});

export type QuestionnaireRequestAssetsResponse = z.infer<
  typeof QuestionnaireRequestAssetsResponseSchema
>;

/**
 * Path parameters of
 * `GET /api/questionnaires/requests/:requestId/assets/:assetId/file`.
 *
 * The asset id, never a file url. That is the difference between this route and
 * the `GET /v3/files/:fileUrl` signer it replaced for these pages: the object key
 * is resolved server-side from an asset the request is allowed to reach, so a
 * caller cannot name a key of its own. That signer was removed in DELTA-2422.
 */
export const QuestionnaireRequestAssetFileParamsSchema = z.object({
  requestId: z.string().min(1).max(64),
  assetId: z.string().min(1).max(64),
});

export type QuestionnaireRequestAssetFileParams = z.infer<
  typeof QuestionnaireRequestAssetFileParamsSchema
>;

/**
 * Which of an asset's files to sign.
 *
 * Only audits have two — a report and a corrective action plan — and the answer
 * form previews both, so a single "the file" would leave one of them a dead
 * link. Documents and certificates ignore it.
 */
export const REQUEST_ASSET_FILE_PARTS = [
  'report',
  'correctiveActionPlan',
] as const;

export type RequestAssetFilePart = (typeof REQUEST_ASSET_FILE_PARTS)[number];

export const QuestionnaireRequestAssetFileQuerySchema = z.object({
  bucket: z.enum(REQUEST_ASSET_BUCKETS),
  part: z.enum(REQUEST_ASSET_FILE_PARTS).optional(),
});

export type QuestionnaireRequestAssetFileQuery = z.infer<
  typeof QuestionnaireRequestAssetFileQuerySchema
>;

/** Shaped like the signer bodies elsewhere — `{ signedUrl }` — so callers can swap
 *  one for the other (`GET /api/claims/:id/file` returns the same). */
export const QuestionnaireRequestAssetFileResponseSchema = z.object({
  signedUrl: z.string(),
});

export type QuestionnaireRequestAssetFileResponse = z.infer<
  typeof QuestionnaireRequestAssetFileResponseSchema
>;
